import { Fragment, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { getDespesasResumoMensal, getRealizadoErros } from "../../api";
import RealizadoLayout from "../../components/gestaoRealizado/RealizadoLayout";
import SincronizacaoDespesas from "../../components/gestaoRealizado/SincronizacaoDespesas";
import {
  ANO_ATUAL, AvisoErro, BarraFiltros, Card, Carregando, GradeKpis, Kpi, MESES, Seletor, Vazio, botaoPequeno, estiloTabela,
  formatCompacto, formatValor, opcoesAno, opcoesEmpresa, useCarregar, useTema,
} from "../../components/gestaoRealizado/comum";

// Visão geral das despesas realizadas (Fase 2): Contas a Pagar do Omie por MÊS DE EMISSÃO,
// classificado pelo código do item (descrição da categoria) e pelo departamento da plataforma
// (de-para). Cancelados e títulos removidos do Omie não entram. Por item, as linhas ficam sob a
// categoria pai como em Despesas Gerais / Custos Gerais (pai do plano de contas; item fora do
// plano usa a categoria superior do Omie). Clicar num valor de item abre os lançamentos daquela
// célula. Ver backend/services/realizado_despesas_omie.py.

const SEM = "__sem__";
const ORIGEM_ERROS = "SYNC_OMIE_DESPESAS";
// Mesmas cores da categoria pai / subcategoria da Gestão Orçamentária.
const corPai = (isLight) => (isLight ? "#EDE0FB" : "#251A45");

// "2.1.10" depois de "2.1.9"; sem código (pai "Balanço" do Omie) depois; "sem código do item" por último.
const compararCodigo = (a, b) => {
  if (a === b) return 0;
  if (a === SEM) return 1;
  if (b === SEM) return -1;
  if (!/^\d/.test(a)) return /^\d/.test(b) ? 1 : a.localeCompare(b);
  if (!/^\d/.test(b)) return -1;
  const pa = a.split(".").map(Number);
  const pb = b.split(".").map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const d = (pa[i] ?? -1) - (pb[i] ?? -1);
    if (d) return d;
  }
  return 0;
};

export default function DespesasRealizado() {
  const [isLight, setIsLight, C] = useTema();
  const navigate = useNavigate();
  const [ano, setAno] = useState(ANO_ATUAL);
  const [empresa, setEmpresa] = useState("");
  const [linhasPor, setLinhasPor] = useState("item");
  const [filtroDep, setFiltroDep] = useState("");
  const [abertos, setAbertos] = useState(() => new Set());
  const { dados, carregando, erro, recarregar } = useCarregar(() => getDespesasResumoMensal(ano, empresa), [ano, empresa]);
  const pendencias = useCarregar(() => getRealizadoErros({ situacao: "pendentes", origem: ORIGEM_ERROS }), []);
  const T = estiloTabela(C);

  const aoConcluir = () => { recarregar(); pendencias.recarregar(); };

  const departamentos = useMemo(
    () => [...new Set((dados || []).map((r) => r.departamento || SEM))].sort((a, b) => (a === SEM ? 1 : b === SEM ? -1 : a.localeCompare(b))),
    [dados],
  );

  // Matriz × mês, já com o filtro do outro eixo aplicado. Por item: grupos = categoria pai, com
  // os itens dentro; por departamento: um grupo por departamento, sem filhos.
  const { grupos, totaisMes, totalGeral } = useMemo(() => {
    const novaLinha = (extra) => ({ ...extra, meses: Array(12).fill(0), total: 0 });
    const somar = (l, r) => { l.meses[r.mes - 1] += r.valor; l.total += r.valor; };
    const porGrupo = new Map();
    const totais = Array(12).fill(0);
    (dados || []).forEach((r) => {
      const dep = r.departamento || SEM;
      if (linhasPor === "item" && filtroDep && dep !== filtroDep) return;
      let chaveGrupo;
      let grupoBase;
      if (linhasPor !== "item") {
        chaveGrupo = dep;
        grupoBase = { chave: dep, codigo: null, nome: dep === SEM ? null : dep };
      } else if (!r.codigoItem) {
        chaveGrupo = SEM;
        grupoBase = { chave: SEM, codigo: null, nome: null };
      } else {
        chaveGrupo = r.paiCodigo || r.paiNome || "Outros";
        grupoBase = { chave: chaveGrupo, codigo: r.paiCodigo, nome: r.paiNome || "Outros" };
      }
      if (!porGrupo.has(chaveGrupo)) porGrupo.set(chaveGrupo, novaLinha({ ...grupoBase, filhos: new Map() }));
      const g = porGrupo.get(chaveGrupo);
      somar(g, r);
      if (linhasPor === "item" && r.codigoItem) {
        if (!g.filhos.has(r.codigoItem)) g.filhos.set(r.codigoItem, novaLinha({ chave: r.codigoItem, nome: r.nomeItem }));
        somar(g.filhos.get(r.codigoItem), r);
      }
      totais[r.mes - 1] += r.valor;
    });
    const lista = [...porGrupo.values()].map((g) => ({ ...g, filhos: [...g.filhos.values()].sort((a, b) => compararCodigo(a.chave, b.chave)) }));
    lista.sort((a, b) => (linhasPor === "item" ? compararCodigo(a.chave, b.chave) : (a.chave === SEM) - (b.chave === SEM) || b.total - a.total));
    return { grupos: lista, totaisMes: totais, totalGeral: totais.reduce((a, b) => a + b, 0) };
  }, [dados, linhasPor, filtroDep]);

  const alternarGrupo = (chave) => setAbertos((s) => {
    const n = new Set(s);
    if (n.has(chave)) n.delete(chave); else n.add(chave);
    return n;
  });
  const comFilhos = grupos.filter((g) => g.filhos.length);
  const todosAbertos = comFilhos.length > 0 && comFilhos.every((g) => abertos.has(g.chave));

  const total = (dados || []).reduce((a, r) => a + r.valor, 0);
  const semCodigo = (dados || []).filter((r) => !r.codigoItem).reduce((a, r) => a + r.valor, 0);
  const semDep = (dados || []).filter((r) => !r.departamento).reduce((a, r) => a + r.valor, 0);
  const qtdPendencias = pendencias.dados?.erros?.length || 0;

  const abrir = (chave, mes) => {
    // sem mês = coluna do ano; a tela de lançamentos abre no mês atual se ?mes faltar
    const params = new URLSearchParams({ ano, ...(empresa && { empresa }), mes: mes || "todos" });
    const dep = linhasPor === "item" ? filtroDep : chave;
    if (linhasPor === "item") params.set("item", chave);
    if (dep) params.set("departamento", dep);
    navigate(`/gestao-realizado/despesas/lancamentos?${params}`);
  };

  const celula = (valor, chave, mes) => (
    <button
      onClick={() => abrir(chave, mes)} disabled={!valor} title={valor ? "Ver lançamentos" : undefined}
      style={{ background: "none", border: "none", padding: 0, color: "inherit", font: "inherit", cursor: valor ? "pointer" : "default", textDecoration: valor ? "underline dotted" : "none" }}
    >
      {formatValor(valor)}
    </button>
  );

  return (
    <RealizadoLayout titulo="Despesas" isLight={isLight} setIsLight={setIsLight}>
      <BarraFiltros>
        <Seletor C={C} rotulo="Ano da emissão" value={ano} onChange={(v) => setAno(Number(v))} opcoes={opcoesAno} />
        <Seletor C={C} rotulo="Empresa" value={empresa} onChange={setEmpresa} opcoes={opcoesEmpresa} />
        <Seletor C={C} rotulo="Linhas por" value={linhasPor} onChange={(v) => { setLinhasPor(v); setFiltroDep(""); }} opcoes={[
          { value: "item", label: "Item do plano" }, { value: "departamento", label: "Departamento" },
        ]} />
        {linhasPor === "item" && (
          <Seletor C={C} rotulo="Departamento" value={filtroDep} onChange={setFiltroDep} opcoes={[
            { value: "", label: "Todos" }, ...departamentos.map((d) => ({ value: d, label: d === SEM ? "Sem departamento" : d })),
          ]} />
        )}
      </BarraFiltros>
      <SincronizacaoDespesas C={C} onConcluido={aoConcluir} />
      <AvisoErro C={C}>{erro}</AvisoErro>

      {carregando && !dados ? <Carregando C={C} /> : dados && (
        <>
          <GradeKpis>
            <Kpi C={C} rotulo={`Despesas ${ano}`} valor={formatCompacto(total)} detalhe="Contas a Pagar por emissão, sem cancelados" />
            <Kpi C={C} rotulo="Sem código do item" valor={formatCompacto(semCodigo)} detalhe="Categoria do plano antigo no Omie" cor={semCodigo ? C.warning : C.success} />
            <Kpi C={C} rotulo="Sem departamento" valor={formatCompacto(semDep)} detalhe="Sem departamento no Omie ou sem de-para" cor={semDep ? C.warning : C.success} />
            <Kpi C={C} rotulo="Pendências" valor={qtdPendencias} detalhe="Despesas: erros não resolvidos no log" cor={qtdPendencias ? C.danger : C.success} />
          </GradeKpis>

          <Card
            C={C}
            titulo={`Despesas realizadas · ${ano}${empresa ? ` · ${empresa}` : ""}${filtroDep ? ` · ${filtroDep === SEM ? "sem departamento" : filtroDep}` : ""}`}
            subtitulo={linhasPor === "item"
              ? "Mês de emissão do título no Omie. Clique na categoria pai para abrir as subcategorias e num valor para ver os lançamentos."
              : "Mês de emissão do título no Omie. Clique num valor para ver os lançamentos."}
            acoes={linhasPor === "item" && comFilhos.length > 0 && (
              <button style={botaoPequeno(C.brand, true)} onClick={() => setAbertos(todosAbertos ? new Set() : new Set(comFilhos.map((g) => g.chave)))}>
                {todosAbertos ? "Recolher tudo" : "Expandir tudo"}
              </button>
            )}
          >
            {grupos.length === 0 ? <Vazio C={C}>Nenhuma despesa sincronizada para {ano}. Use “Sincronizar agora” acima.</Vazio> : (
              <div style={{ ...T.wrap, maxHeight: "65vh" }}>
                <table style={{ ...T.table, minWidth: 1300 }}>
                  <thead>
                    <tr>
                      <th style={{ ...T.th, position: "sticky", left: 0, zIndex: 2 }}>{linhasPor === "item" ? "Categoria / item do plano" : "Departamento"}</th>
                      {MESES.map((m) => <th key={m} style={{ ...T.th, ...T.thNum }}>{m}</th>)}
                      <th style={{ ...T.th, ...T.thNum }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grupos.map((g) => {
                      const sem = g.chave === SEM;
                      const temFilhos = g.filhos.length > 0;
                      const aberto = abertos.has(g.chave);
                      // Linha "folha" (departamento ou "sem código do item"): valores clicáveis.
                      if (!temFilhos) {
                        const fundo = sem ? `${C.warning}12` : C.card;
                        return (
                          <tr key={g.chave} style={{ background: fundo }}>
                            <td style={{ ...T.td, position: "sticky", left: 0, background: sem ? C.card : fundo, minWidth: 280, maxWidth: 360 }}>
                              {sem ? <span style={{ color: C.warning, fontWeight: 700 }}>{linhasPor === "item" ? "Sem código do item" : "Sem departamento"}</span> : g.nome}
                            </td>
                            {g.meses.map((v, i) => <td key={MESES[i]} style={{ ...T.td, ...T.tdNum }}>{celula(v, g.chave, i + 1)}</td>)}
                            <td style={{ ...T.td, ...T.tdNum, fontWeight: 700 }}>{celula(g.total, g.chave, null)}</td>
                          </tr>
                        );
                      }
                      const fundoPai = corPai(isLight);
                      return (
                        <Fragment key={g.chave}>
                          <tr onClick={() => alternarGrupo(g.chave)} style={{ background: fundoPai, fontWeight: 700, cursor: "pointer" }}>
                            <td style={{ ...T.td, position: "sticky", left: 0, background: fundoPai, minWidth: 280, maxWidth: 360 }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                {aberto ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                {g.codigo && <span style={{ fontVariantNumeric: "tabular-nums" }}>{g.codigo}</span>}
                                {g.nome}
                                {!aberto && <span style={{ fontSize: 11, fontWeight: 500, color: C.muted }}>({g.filhos.length} {g.filhos.length === 1 ? "subcategoria" : "subcategorias"})</span>}
                              </span>
                            </td>
                            {g.meses.map((v, i) => <td key={MESES[i]} style={{ ...T.td, ...T.tdNum }}>{formatValor(v)}</td>)}
                            <td style={{ ...T.td, ...T.tdNum }}>{formatValor(g.total)}</td>
                          </tr>
                          {aberto && g.filhos.map((f) => (
                            <tr key={f.chave}>
                              <td style={{ ...T.td, position: "sticky", left: 0, background: C.card, minWidth: 280, maxWidth: 360, paddingLeft: 32 }}>
                                <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{f.chave}</span> <span style={{ color: C.muted }}>{f.nome || ""}</span>
                              </td>
                              {f.meses.map((v, i) => <td key={MESES[i]} style={{ ...T.td, ...T.tdNum }}>{celula(v, f.chave, i + 1)}</td>)}
                              <td style={{ ...T.td, ...T.tdNum, fontWeight: 700 }}>{celula(f.total, f.chave, null)}</td>
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })}
                    <tr style={T.total}>
                      <td style={{ ...T.td, position: "sticky", left: 0, background: C.rowAlt }}>Total</td>
                      {totaisMes.map((v, i) => <td key={MESES[i]} style={{ ...T.td, ...T.tdNum }}>{formatValor(v)}</td>)}
                      <td style={{ ...T.td, ...T.tdNum }}>{formatValor(totalGeral)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
              Categoria pai igual à Gestão Orçamentária (ex.: 2.1.11 Comissão fica em 2.6 Administrativas Gerais). Itens que não estão no plano
              de contas da plataforma (2.1 Salários e Encargos, 2.2 Benefícios, impostos, 6.1 Outras Saídas) ficam sob a categoria superior do Omie.
            </div>
          </Card>
        </>
      )}
    </RealizadoLayout>
  );
}
